import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const Login = () => {
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState('');

  // Check for suspension message on mount
  useEffect(() => {
    const message = sessionStorage.getItem('suspension_message');
    if (message) {
      setSuspensionMessage(message);
      sessionStorage.removeItem('suspension_message'); // Clear after displaying
    }
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Call the login API
      const response = await api.post('/auth/login', formData);
      
      if (response.data.token) {
        // Use context login method
        login(response.data.user, response.data.token);
        
        // Redirect to home page
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Extract error message from backend
      // Laravel validation errors come in error.response.data.errors.email array
      // or error.response.data.message
      let errorMessage = 'Identifiants incorrects. Veuillez réessayer.';
      
      if (error.response?.data?.errors?.email) {
        // Validation error (including lockout messages)
        const emailErrors = error.response.data.errors.email;
        if (Array.isArray(emailErrors) && emailErrors.length > 0) {
          errorMessage = emailErrors[0];
        } else if (typeof emailErrors === 'string') {
          errorMessage = emailErrors;
        }
      } else if (error.response?.data?.message) {
        // General error message
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <style>{`
        /* Hide browser's default password visibility toggle */
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
        
        /* Hide WebKit password visibility toggle */
        input[type="password"]::-webkit-credentials-auto-fill-button,
        input[type="password"]::-webkit-credentials-auto-fill-button:hover {
          display: none !important;
        }
        
        /* Ensure our custom button is always visible */
        .password-toggle-btn {
          pointer-events: auto !important;
          z-index: 20 !important;
        }
      `}</style>
      {/* Dotted background pattern */}
      <div 
        className="absolute inset-0 opacity-50 z-0"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(239, 68, 68, 0.15) 3px, transparent 3px)',
          backgroundSize: '25px 25px'
        }}
      />
      
      {/* Header */}
      <header className="relative z-10 flex justify-between items-center p-6 animate-fade-in">
        <div className="flex items-center space-x-8">
          <Link 
            to="/" 
            className="text-2xl font-bold text-gray-800 hover:text-red-600 transition-colors duration-300 ease-in-out"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
        </div>
        <div className="flex items-center space-x-4">
          
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] animate-slide-up">
        <div className="w-full max-w-md mx-4">
          {/* Login Card */}
          <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-red-600 mb-2">
                SE CONNECTER
              </h1>
              <p className="text-gray-600">
                Connectez-vous à votre compte
              </p>
            </div>

            {/* Suspension Message */}
            {suspensionMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-xl">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{suspensionMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse e-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 outline-none"
                  placeholder="votre@email.com"
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 outline-none"
                    placeholder="Votre mot de passe"
                    style={{
                      WebkitTextSecurity: showPassword ? 'none' : 'disc',
                      msRevealType: 'none'
                    }}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="password-toggle-btn absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    <FontAwesomeIcon 
                      icon={showPassword ? faEyeSlash : faEye} 
                      className="w-5 h-5"
                    />
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-red-600 hover:text-red-700 transition-colors duration-200"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-200 ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-red-600 hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connexion...
                  </div>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
